import { createFileRoute } from "@tanstack/react-router";
import { Plus, Star } from "lucide-react";
import { toast } from "sonner";
import { useEffect } from "react";
import { AdminShell } from "@/components/admin/admin-shell";
import { Pill } from "@/components/admin/status-badge";
import { RegisterRiderSheet } from "@/components/admin/register-rider-form";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useRiders, useSetRiderLifecycle } from "@/lib/queries";
import { formatDate, formatPhone } from "@/lib/format";
import { useUIStore } from "@/store/ui";

export const Route = createFileRoute("/riders")({
  component: RidersPage,
});

function RidersPage() {
  const { data: riders, isLoading } = useRiders();
  const setLifecycle = useSetRiderLifecycle();
  const setRegisterOpen = useUIStore((s) => s.setRegisterRiderOpen);
  const selectedRiderId = useUIStore((s) => s.selectedRiderId);
  const setSelectedRiderId = useUIStore((s) => s.setSelectedRiderId);

  const selected = (riders ?? []).find((r) => r.linkedUid === selectedRiderId) ?? null;

  // Set page title on mount
  useEffect(() => {
    document.title = "Riders — 24Boda Admin";
  }, []);

  return (
    <AdminShell
      title="Riders"
      description="Fleet management and approvals"
      actions={
        <Button onClick={() => setRegisterOpen(true)}>
          <Plus className="size-4" />
          Register rider
        </Button>
      }
    >
      <Card className="overflow-x-auto rounded-lg p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Vehicle</TableHead>
              <TableHead>Plate</TableHead>
              <TableHead>Rating</TableHead>
              <TableHead>Trips</TableHead>
              <TableHead>Approval</TableHead>
              <TableHead>Account</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={9} className="py-10 text-center text-muted-foreground">
                  Loading riders…
                </TableCell>
              </TableRow>
            )}
            {!isLoading && (riders?.length ?? 0) === 0 && (
              <TableRow>
                <TableCell colSpan={9} className="py-10 text-center text-muted-foreground">
                  No riders registered yet.
                </TableCell>
              </TableRow>
            )}
            {(riders ?? []).map((r) => (
              <TableRow key={r.id}>
                <TableCell className="font-medium">{r.displayName || "Unnamed"}</TableCell>
                <TableCell>{formatPhone(r.phoneE164)}</TableCell>
                <TableCell className="capitalize">{r.vehicleType}</TableCell>
                <TableCell className="uppercase">{r.plateNumber}</TableCell>
                <TableCell>
                  <span className="inline-flex items-center gap-1">
                    <Star className="size-3.5 fill-warning text-warning" />
                    {Number(r.profile?.ratingAverage ?? 0).toFixed(1)}
                  </span>
                </TableCell>
                <TableCell>{r.profile?.completedShipmentCount ?? 0}</TableCell>
                <TableCell>
                  <Pill tone={r.status === "approved" ? "success" : "warning"}>
                    {r.status.replace("_", " ")}
                  </Pill>
                </TableCell>
                <TableCell>
                  <Pill tone={r.user?.status === "active" ? "success" : "neutral"}>
                    {(r.user?.status ?? "pending_onboarding").replace("_", " ")}
                  </Pill>
                </TableCell>
                <TableCell className="space-x-2 text-right whitespace-nowrap">
                  {r.status !== "approved" && (
                    <Button
                      size="sm"
                      disabled={!r.documentPaths["nationalId"] || setLifecycle.isPending}
                      onClick={() =>
                        setLifecycle.mutate(
                          { uid: r.linkedUid, action: "approveRider" },
                          {
                            onSuccess: () => toast.success("Rider approved"),
                            onError: () => toast.error("Could not approve rider"),
                          },
                        )
                      }
                    >
                      Approve
                    </Button>
                  )}
                  {r.status === "approved" && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={setLifecycle.isPending}
                      onClick={() => {
                        const suspended = r.profile?.approvalStatus === "suspended";
                        setLifecycle.mutate(
                          {
                            uid: r.linkedUid,
                            action: suspended ? "reactivateRider" : "suspendRider",
                          },
                          {
                            onSuccess: () =>
                              toast.success(suspended ? "Rider reactivated" : "Rider suspended"),
                            onError: () => toast.error("Could not update rider"),
                          },
                        );
                      }}
                    >
                      {r.profile?.approvalStatus === "suspended" ? "Reactivate" : "Suspend"}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setSelectedRiderId(r.linkedUid)}
                  >
                    Details
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <RegisterRiderSheet />

      <Dialog open={Boolean(selected)} onOpenChange={(o) => !o && setSelectedRiderId(null)}>
        <DialogContent className="rounded-xl">
          <DialogHeader>
            <DialogTitle>{selected?.displayName || "Rider details"}</DialogTitle>
            <DialogDescription>{formatPhone(selected?.phoneE164)}</DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm">
              <Detail label="Vehicle" value={selected.vehicleType} />
              <Detail label="Plate" value={selected.plateNumber} />
              <Detail label="Application" value={selected.status.replace("_", " ")} />
              <Detail label="Account" value={(selected.user?.status ?? "pending_onboarding").replace("_", " ")} />
              <Detail label="Rating" value={Number(selected.profile?.ratingAverage ?? 0).toFixed(1)} />
              <Detail label="Completed deliveries" value={String(selected.profile?.completedShipmentCount ?? 0)} />
              <Detail label="Joined" value={formatDate(selected.createdAt)} />
              <Detail
                label="National ID"
                value={selected.documentPaths["nationalId"] ? "Uploaded" : "Missing"}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminShell>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between border-b border-border pb-2 last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium capitalize text-foreground">{value}</span>
    </div>
  );
}
